import { ChevronDown } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '@components/ui/dropdown-menu';
import { cn } from '@core/utils/cn';
import { I18NText } from '@api/ChallengeClient';
import { ReactComponent as DE } from '../../../assets/svg/flags/de.svg';
import { ReactComponent as PL } from '../../../assets/svg/flags/pl.svg';
import { ReactComponent as EN } from '../../../assets/svg/flags/us.svg';
import { ReactComponent as NO } from '../../../assets/svg/flags/no.svg';
import { ReactComponent as DK } from '../../../assets/svg/flags/dk.svg';
import { Option } from '../select';

export type TLanguage = keyof I18NText;

export interface LanguageOption extends Option<TLanguage> {
    filled?: boolean;
    main?: boolean;
}

interface LanguageSelectProps {
    options: LanguageOption[];
    value: TLanguage;
    onChange(language: TLanguage): void;
}

export function getLanguageIcon(language: TLanguage) {
    switch (language) {
        case 'de':
            return DE;
        case 'en':
            return EN;
        case 'pl':
            return PL;
        case 'nb':
            return NO;
        case 'da':
            return DK;
    }
}

const Flag = ({ language }: { language: TLanguage }) => {
    const Icon = getLanguageIcon(language);
    return Icon ? <Icon viewBox="0 0 21 15" className="h-4 w-6" /> : null;
};

const LanguageSelect = observer(
    ({ value, options, onChange }: LanguageSelectProps) => {
        const allFilled = options.every((option) => option.filled);
        return (
            <DropdownMenu>
                <DropdownMenuTrigger
                    className="flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-accent data-[state=open]:[&>svg]:rotate-180"
                    data-testid="language-dropdown-button"
                >
                    <ChevronDown
                        className={cn(
                            'size-5 transition-transform',
                            allFilled ? 'text-success' : 'text-destructive'
                        )}
                    />
                    <Flag language={value} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                    {options.map((option) => (
                        <DropdownMenuItem
                            key={option.label}
                            data-testid="language-select-option"
                            onSelect={() => onChange(option.value)}
                        >
                            <span
                                className={cn(
                                    'size-2 rounded-full',
                                    option.filled
                                        ? 'bg-success'
                                        : 'bg-destructive'
                                )}
                            />
                            {option.label}
                            <Flag language={option.value} />
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }
);

export default LanguageSelect;
